import { useState } from "react";
import { Mail, ArrowUpRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";
import { displayUrl, fieldIcon, fieldLabel } from "../../lib/fieldMeta";
import { track } from "../../lib/track";

const HIDDEN = new Set(["department_en", "department_ar", "bio_en", "bio_ar", "extension", "maps_url"]);

const rowsFor = (card, lang) => {
  const v = Object.fromEntries(card.fields.map((f) => [f.key, f.value]));
  const locKey = lang === "ar" && v.office_location_ar ? "office_location_ar" : "office_location_en";
  const rows = [{ key: "email", icon: Mail, label: lang === "ar" ? "البريد الإلكتروني" : "Email", href: `mailto:${card.email}`, value: card.email }];
  for (const f of card.fields) {
    if (HIDDEN.has(f.key) || f.group === "social") continue;
    if (f.key.startsWith("office_location") && f.key !== locKey) continue;
    if (!v[locKey] && f.key.startsWith("office_location")) continue;
    let href = f.type === "url" ? f.value : null;
    let value = f.type === "url" ? displayUrl(f.value) : f.value;
    if (f.type === "email") href = `mailto:${f.value}`;
    if (f.type === "tel") href = f.key === "whatsapp" ? `https://wa.me/${f.value.replace(/\D/g, "")}` : `tel:${f.value.replace(/[^\d+]/g, "")}`;
    if (f.key === "office_phone" && v.extension) value = `${f.value}  ext. ${v.extension}`;
    if (f.key.startsWith("office_location")) href = v.maps_url || null;
    rows.push({ key: f.key, icon: fieldIcon(f), label: fieldLabel(f, lang), href, value, dir: f.dir, multiline: f.type === "textarea" });
  }
  return rows;
};

const COPYABLE = new Set(["phone", "email"]);

const CopyButton = ({ row, slug, lang }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(row.value);
    toast.success(lang === "ar" ? "تم النسخ" : "Copied");
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  const Icon = done ? Check : Copy;
  return (
    <button type="button" onClick={copy} aria-label={lang === "ar" ? "نسخ" : "Copy"} data-testid={`copy-${row.key}`}
      className="btn-press me-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
      <Icon className={`h-4 w-4 ${done ? "text-emerald-600" : ""}`} />
    </button>
  );
};

const Row = ({ row, slug, lang }) => {
  const Tag = row.href ? "a" : "div";
  const Icon = row.icon;
  return (
    <li className="flex items-center">
      <Tag href={row.href || undefined} target={row.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
        onClick={row.href ? () => track(slug, { type: "click", target: row.key }) : undefined}
        data-testid={`contact-${row.key}`}
        className="group flex min-h-[64px] min-w-0 flex-1 items-center gap-4 px-5 py-3 transition-colors duration-200 hover:bg-slate-50">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors group-hover:bg-slate-900 group-hover:text-white">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">{row.label}</span>
          <span dir={row.dir || "ltr"} className={`block text-start text-sm text-slate-800 ${row.multiline ? "whitespace-pre-line" : "truncate"} ${row.dir === "rtl" ? "font-arabic" : row.multiline ? "" : "font-mono"}`}>{row.value}</span>
        </span>
        {row.href && <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:-translate-y-0.5 rtl:-scale-x-100" />}
      </Tag>
      {COPYABLE.has(row.key) && row.value && <CopyButton row={row} slug={slug} lang={lang} />}
    </li>
  );
};

const SocialGrid = ({ socials, lang, slug }) => (
  <div className="grid grid-cols-4 gap-3" data-testid="social-links">
    {socials.map((f) => {
      const Icon = fieldIcon(f);
      return (
        <a key={f.key} href={f.value} target="_blank" rel="noopener noreferrer" data-testid={`social-${f.key}`} title={fieldLabel(f, lang)}
          onClick={() => track(slug, { type: "click", target: f.key })}
          className="btn-press flex aspect-square flex-col items-center justify-center gap-1.5 rounded-2xl border border-slate-200/80 bg-white text-slate-700 hover:border-slate-900 hover:text-slate-900">
          <Icon className="h-5 w-5" />
          <span className="text-[10px] font-semibold">{fieldLabel(f, lang)}</span>
        </a>
      );
    })}
  </div>
);

export const ContactList = ({ card }) => {
  const { lang } = useLang();
  const socials = card.fields.filter((f) => f.group === "social");
  return (
    <div className="space-y-4">
      <ul className="divide-y divide-slate-200/80 overflow-hidden rounded-3xl border border-slate-200/80 bg-white" data-testid="contact-list">
        {rowsFor(card, lang).map((row) => <Row key={row.key} row={row} slug={card.slug} lang={lang} />)}
      </ul>
      {socials.length > 0 && <SocialGrid socials={socials} lang={lang} slug={card.slug} />}
    </div>
  );
};
